(() => {
  const REGLOSS_BUNDLE_CTA_SELECTOR =
    '.lb-bundle-cta, [class*="lb-bundle-cta"], .lb-widget-bl, .lb-widget-pag, [class*="lb-widget-bl"], [class*="lb-widget-pag"]';
  const REGLOSS_CART_REDIRECT_MS = 20000;

  let reglossSuppressCartRedirectUntil = 0;
  let reglossCheckoutFlowTimer = 0;
  /** True only during ATC / bundle-add until drawer opens or shopper dismisses it. */
  let reglossExpectCartDrawerOpenAfterSync = false;

  const reglossIsCartDrawerExperience = () =>
    document.documentElement.classList.contains("ab-cart-drawer");

  const reglossArmCartRedirectSuppression = (
    durationMs = REGLOSS_CART_REDIRECT_MS,
  ) => {
    reglossSuppressCartRedirectUntil = Date.now() + durationMs;
  };

  const reglossClearCartDrawerCheckoutFlow = () => {
    window.clearTimeout(reglossCheckoutFlowTimer);
    document.body.classList.remove("regloss-cart-drawer-pending");
  };

  /** From PDP/quick-add ATC until drawer opens — suppress BOGOS / cart notification flash. */
  const reglossDismissCartDrawerAutoOpen = () => {
    reglossExpectCartDrawerOpenAfterSync = false;
    reglossClearCartDrawerCheckoutFlow();
  };

  const reglossBeginCartDrawerCheckoutFlow = () => {
    if (!reglossIsCartDrawerExperience()) return;
    reglossArmCartRedirectSuppression();
    reglossExpectCartDrawerOpenAfterSync = true;
    document.body.classList.add("regloss-cart-drawer-pending");
    window.clearTimeout(reglossCheckoutFlowTimer);
    reglossCheckoutFlowTimer = window.setTimeout(
      reglossClearCartDrawerCheckoutFlow,
      15000,
    );
  };

  const reglossCartPath = () =>
    (window.routes?.cart_url || "/cart").replace(/\/+$/, "");

  const REGLOSS_LB_PRODUCT_KEY = "_lb-product";
  const REGLOSS_RECOMMEND_BUNDLE_KEY = "_recommend_fuelerate_bundle";

  /**
   * Stamp `_lb-product` when a bundle offer is already in play (kit PDP, drawer
   * config/cache, or upsells visible). SellEasy needs this for BUNDLEOFFER.
   * Progress UI must not treat every stamped line as a tier item — see panel.
   */
  const reglossShouldInjectBundleLineProperties = () =>
    Boolean(
      document.querySelector('[data-regloss-fuel-bundle-offer="true"]'),
    ) ||
    Boolean(window.__reglossBundleLineProperties?.[REGLOSS_LB_PRODUCT_KEY]) ||
    Boolean(reglossGetConfigLbProduct()) ||
    Boolean(
      document.querySelector(
        "regloss-cart-drawer .regloss-cart-drawer__upsells",
      ),
    );

  const reglossReadLineProperty = (properties, key) => {
    if (!properties) return "";
    const value = properties[key];
    if (value != null && String(value).trim() !== "") return String(value);
    return "";
  };

  const reglossExtractLbProductFromCartItems = (items = []) => {
    for (const item of items) {
      const lbProduct = reglossReadLineProperty(
        item.properties,
        REGLOSS_LB_PRODUCT_KEY,
      );
      if (lbProduct) return lbProduct;
    }
    return "";
  };

  const reglossReadCartDrawerConfig = () => {
    const node = document.getElementById("ReglossCartDrawerConfig");
    if (!node?.textContent?.trim()) return null;
    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  };

  const reglossGetConfigLbProduct = () => {
    const config = reglossReadCartDrawerConfig();
    return reglossReadLineProperty(
      config?.bundleLineProperties,
      REGLOSS_LB_PRODUCT_KEY,
    );
  };

  const reglossGetPageLbProduct = () => {
    const form = document.querySelector(
      "product-form[data-regloss-lb-product]",
    );
    const value = form?.getAttribute("data-regloss-lb-product");
    return value?.trim() ? value.trim() : "";
  };

  const reglossMergeFuelerateBundleLineProperties = (
    existing = {},
    { asBundleRec = false } = {},
  ) => {
    const merged = { ...existing };

    const lbProduct =
      reglossReadLineProperty(existing, REGLOSS_LB_PRODUCT_KEY) ||
      window.__reglossBundleLineProperties?.[REGLOSS_LB_PRODUCT_KEY] ||
      reglossGetConfigLbProduct() ||
      reglossGetPageLbProduct();

    if (lbProduct) {
      merged[REGLOSS_LB_PRODUCT_KEY] = lbProduct;
    }

    const existingRecommend = reglossReadLineProperty(
      existing,
      REGLOSS_RECOMMEND_BUNDLE_KEY,
    );
    if (asBundleRec || existingRecommend) {
      merged[REGLOSS_RECOMMEND_BUNDLE_KEY] = existingRecommend || "true";
    }

    return merged;
  };

  const reglossUpdateBundleLinePropertiesCacheFromItems = (items = []) => {
    const lbProduct = reglossExtractLbProductFromCartItems(items);
    if (!lbProduct) return;

    window.__reglossBundleLineProperties =
      reglossMergeFuelerateBundleLineProperties({
        [REGLOSS_LB_PRODUCT_KEY]: lbProduct,
      });
  };

  const reglossRefreshBundleLinePropertiesCache = async () => {
    try {
      const cart = await fetch(`${reglossCartPath()}.js`).then((response) =>
        response.json(),
      );
      reglossUpdateBundleLinePropertiesCacheFromItems(cart.items || []);
      return window.__reglossBundleLineProperties || null;
    } catch {
      return null;
    }
  };

  const reglossSyncBundleLinePropertiesFromConfig = () => {
    const lbProduct = reglossGetConfigLbProduct();
    if (!lbProduct) return null;

    window.__reglossBundleLineProperties = {
      [REGLOSS_LB_PRODUCT_KEY]: lbProduct,
    };
    return window.__reglossBundleLineProperties;
  };

  const reglossBuildFuelerateBundleLineProperties = async () => {
    const fromConfig = reglossSyncBundleLinePropertiesFromConfig();
    if (fromConfig?.[REGLOSS_LB_PRODUCT_KEY]) {
      return { ...fromConfig };
    }

    const cached = window.__reglossBundleLineProperties;
    if (cached?.[REGLOSS_LB_PRODUCT_KEY]) {
      return { ...cached };
    }

    const fromCart = await reglossRefreshBundleLinePropertiesCache();
    if (fromCart?.[REGLOSS_LB_PRODUCT_KEY]) {
      return { ...fromCart };
    }

    return reglossMergeFuelerateBundleLineProperties({});
  };

  /**
   * SellEasy Large Bundle discounts are Shopify Function codes (e.g. BUNDLEOFFER).
   * Widget ATC calls processOffer/handleDiscount then applies the code via Storefront API.
   * Drawer upsells only set `_lb-product` — without applying the code, cart stays full price.
   * Never dispatch empty `lbSECartUpdated` (SellEasy requires detail.cart / detail.isSelleasy).
   */
  const REGLOSS_BUNDLE_CODE_FALLBACKS = {
    a94b0e37: "BUNDLEOFFER",
    "16697b69": "BUNDLEOFFER#4a17",
  };

  const reglossExtractOfferPrefix = (lbProduct = "") => {
    const parts = String(lbProduct).split("#");
    if (parts.length >= 3 && parts[1] === "offer" && parts[2]) {
      return `offer#${parts[2]}`;
    }
    return "";
  };

  const reglossEnsureSellEasySetup = async () => {
    if (window.lbupsellToken && window.lbSetup?.offers?.length) {
      return window.lbSetup;
    }

    if (window.lbSetup?.storeFrontAccessToken) {
      window.lbupsellToken =
        window.lbupsellToken || window.lbSetup.storeFrontAccessToken;
    }

    if (window.lbupsellToken && window.lbSetup?.offers?.length) {
      return window.lbSetup;
    }

    const shop = window.lbShopDomain || window.Shopify?.shop;
    if (!shop) return window.lbSetup || null;

    try {
      const setup = await fetch(
        `/apps/lbupsell/setup?myShopifyDomain=${encodeURIComponent(shop)}`,
      ).then((response) => response.json());
      if (setup?.storeFrontAccessToken) {
        window.lbupsellToken = setup.storeFrontAccessToken;
      }
      if (setup?.offers) {
        window.lbSetup = window.lbSetup || setup;
      }
      return setup;
    } catch {
      return window.lbSetup || null;
    }
  };

  const reglossResolveBundleDiscountCode = async (lbProduct) => {
    if (!lbProduct) return "";

    const setup = await reglossEnsureSellEasySetup();
    const offerPrefix = reglossExtractOfferPrefix(lbProduct);
    const shortId = String(lbProduct).split("#")[2] || "";
    const offers = setup?.offers || window.lbSetup?.offers || [];

    const offer = offers.find((entry) => {
      const recordType = String(entry?.recordType || "");
      return (
        (offerPrefix && recordType.startsWith(offerPrefix)) ||
        (shortId && recordType.includes(shortId))
      );
    });

    const code = offer?.discounts?.code;
    if (code && !offer?.discounts?.isDisabled) return String(code);

    return REGLOSS_BUNDLE_CODE_FALLBACKS[shortId] || "";
  };

  /** Unique products in the SellEasy bundle group (kit qty does not count as extra). */
  const reglossCountBundleUnits = (items = [], lbProduct) => {
    if (!lbProduct) return 0;
    const seen = new Set();
    for (const item of items) {
      const value = reglossReadLineProperty(
        item.properties,
        REGLOSS_LB_PRODUCT_KEY,
      );
      if (value !== lbProduct) continue;
      const productId = item.product_id || item.productId;
      if (productId == null) continue;
      seen.add(String(productId));
    }
    return seen.size;
  };

  const reglossStackSellEasyOfferCode = (code, offerRecordType, cart, lbProduct) => {
    if (!code) return;
    try {
      const existing = JSON.parse(
        localStorage.getItem("lb-offer-codes") || "[]",
      );
      const entry = {
        discount: {
          code,
          trigger: {
            productId: Number(String(lbProduct).split("#")[0]) || undefined,
            check: true,
          },
          products: (cart?.items || [])
            .filter(
              (item) =>
                reglossReadLineProperty(
                  item.properties,
                  REGLOSS_LB_PRODUCT_KEY,
                ) === lbProduct,
            )
            .map((item) => ({
              id: item.variant_id || item.id,
              productId: item.product_id,
            })),
        },
        offer: offerRecordType || code,
      };
      const next = Array.isArray(existing)
        ? existing.filter((row) => row?.offer !== entry.offer)
        : [];
      next.push(entry);
      localStorage.setItem("lb-offer-codes", JSON.stringify(next));
    } catch {
      /* ignore storage failures */
    }
  };

  const reglossApplyBundleDiscountCode = async (cart, code) => {
    if (!cart?.token || !code) return false;

    const existingCodes = (cart.discount_codes || [])
      .map((row) => row?.code)
      .filter(Boolean);
    const alreadyOnCart = (cart.discount_codes || []).some(
      (row) => row?.code === code,
    );
    if (alreadyOnCart) {
      return false;
    }

    await reglossEnsureSellEasySetup();
    const storefrontToken = window.lbupsellToken;
    if (!storefrontToken) return false;

    const discountCodes = [...new Set([...existingCodes, code])];
    const cartGid = `gid://shopify/Cart/${cart.token}`;

    if (typeof window.lbDealeasy?.applyDiscountsToCart === "function") {
      await window.lbDealeasy.applyDiscountsToCart(
        discountCodes,
        "selleasy",
        cartGid,
      );
      return true;
    }

    const query = `
      mutation cartDiscountCodesUpdate($id: ID!, $discountCodes: [String!]!) {
        cartDiscountCodesUpdate(cartId: $id, discountCodes: $discountCodes) {
          cart { id }
          userErrors { field message }
        }
      }
    `;

    await fetch(`/api/2026-04/graphql.json`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": storefrontToken,
      },
      body: JSON.stringify({
        query,
        variables: { id: cartGid, discountCodes },
      }),
    });

    return true;
  };

  const reglossFetchCartJson = () =>
    fetch(`${reglossCartPath()}.js`).then((response) => response.json());

  const reglossCartHasDiscountProgress = (cart, code) => {
    if (!cart) return false;
    if (Number(cart.total_discount) > 0) return true;
    return (cart.discount_codes || []).some(
      (row) => row?.code === code && row?.applicable,
    );
  };

  /** Poll briefly until Ajax cart reflects the applied code — no fixed sleep. */
  const reglossWaitForCartDiscount = async (code, attempts = 6) => {
    for (let i = 0; i < attempts; i += 1) {
      const cart = await reglossFetchCartJson();
      if (reglossCartHasDiscountProgress(cart, code)) return cart;
      if (i < attempts - 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 50));
      }
    }
    return reglossFetchCartJson();
  };

  let reglossBundleSyncInFlight = false;
  let reglossBundleSyncQueued = null;
  const reglossPostCartBundleSync = async ({
    refreshDrawer = false,
  } = {}) => {
    if (!reglossIsCartDrawerExperience()) return;

    if (reglossBundleSyncInFlight) {
      reglossBundleSyncQueued = {
        refreshDrawer:
          Boolean(reglossBundleSyncQueued?.refreshDrawer) || refreshDrawer,
      };
      return;
    }

    reglossBundleSyncInFlight = true;
    try {
      const cart = await reglossFetchCartJson();
      reglossUpdateBundleLinePropertiesCacheFromItems(cart.items || []);

      const lbProduct =
        window.__reglossBundleLineProperties?.[REGLOSS_LB_PRODUCT_KEY] ||
        reglossExtractLbProductFromCartItems(cart.items || []);
      if (!lbProduct) return;

      const bundleUnits = reglossCountBundleUnits(cart.items || [], lbProduct);
      if (bundleUnits < 2) return;

      const code = await reglossResolveBundleDiscountCode(lbProduct);
      if (!code) return;

      const offerPrefix = reglossExtractOfferPrefix(lbProduct);
      const offer = (window.lbSetup?.offers || []).find((entry) =>
        String(entry?.recordType || "").startsWith(offerPrefix),
      );
      reglossStackSellEasyOfferCode(
        code,
        offer?.recordType,
        cart,
        lbProduct,
      );

      const applied = await reglossApplyBundleDiscountCode(cart, code);
      if (!applied || !refreshDrawer) return;

      if (
        !document.body.classList.contains("regloss-cart-drawer-open") &&
        !document.body.classList.contains("regloss-cart-drawer-pending")
      ) {
        return;
      }

      const drawer = document.querySelector("regloss-cart-drawer");
      if (!drawer?.refreshFromServer) return;

      await reglossWaitForCartDiscount(code);
      await drawer.refreshFromServer({ open: true });
    } catch (error) {
      console.error(error);
    } finally {
      reglossBundleSyncInFlight = false;
      const queued = reglossBundleSyncQueued;
      reglossBundleSyncQueued = null;
      if (queued) {
        void reglossPostCartBundleSync(queued);
      }
    }
  };

  const reglossSchedulePostCartBundleSync = ({
    refreshDrawer = false,
  } = {}) => {
    void reglossPostCartBundleSync({ refreshDrawer });
  };

  const reglossNotifyLbCartUpdated = () => {
    void reglossPostCartBundleSync({ refreshDrawer: true });
  };

  const reglossInjectRecommendBundleProperty = (body) => {
    const shouldInject =
      reglossShouldInjectBundleLineProperties() ||
      Boolean(window.__reglossBundleLineProperties?.[REGLOSS_LB_PRODUCT_KEY]) ||
      Boolean(reglossGetConfigLbProduct());

    if (!shouldInject) return body;

    if (body instanceof FormData) {
      const existing = body.get(`properties[${REGLOSS_LB_PRODUCT_KEY}]`);
      const lbProduct =
        (existing != null && String(existing).trim() !== ""
          ? String(existing)
          : "") ||
        window.__reglossBundleLineProperties?.[REGLOSS_LB_PRODUCT_KEY] ||
        reglossGetConfigLbProduct() ||
        reglossGetPageLbProduct();
      if (lbProduct && !body.get(`properties[${REGLOSS_LB_PRODUCT_KEY}]`)) {
        body.append(`properties[${REGLOSS_LB_PRODUCT_KEY}]`, lbProduct);
      }
      return body;
    }

    if (typeof body !== "string" || !body.trim()) return body;

    try {
      const json = JSON.parse(body);

      if (Array.isArray(json.items)) {
        json.items.forEach((item) => {
          item.properties = reglossMergeFuelerateBundleLineProperties(
            item.properties || {},
          );
        });
        return JSON.stringify(json);
      }

      if (json.id) {
        json.properties = reglossMergeFuelerateBundleLineProperties(
          json.properties || {},
        );
        return JSON.stringify(json);
      }
    } catch {
      return body;
    }

    return body;
  };

  window.reglossBuildFuelerateBundleLineProperties =
    reglossBuildFuelerateBundleLineProperties;
  window.reglossMergeFuelerateBundleLineProperties =
    reglossMergeFuelerateBundleLineProperties;
  window.reglossSyncBundleLinePropertiesFromConfig =
    reglossSyncBundleLinePropertiesFromConfig;
  window.reglossNotifyLbCartUpdated = reglossNotifyLbCartUpdated;
  window.reglossUpdateBundleLinePropertiesCacheFromItems =
    reglossUpdateBundleLinePropertiesCacheFromItems;
  window.reglossPostCartBundleSync = reglossPostCartBundleSync;
  window.reglossDismissCartDrawerAutoOpen = reglossDismissCartDrawerAutoOpen;

  const reglossIsCartPageUrl = (url) => {
    try {
      const path = new URL(url, window.location.origin).pathname.replace(
        /\/+$/,
        "",
      );
      return path === reglossCartPath() || path.endsWith("/cart");
    } catch {
      return String(url).includes("/cart");
    }
  };

  const reglossShouldSuppressCartRedirect = (url) => {
    if (!reglossIsCartDrawerExperience()) return false;
    if (Date.now() > reglossSuppressCartRedirectUntil) return false;
    if (!reglossIsCartPageUrl(url)) return false;
    return !String(url).includes("/checkout");
  };

  /** True only while an ATC / cart-sync flow expects the drawer (not after shopper dismisses). */
  const reglossShouldOpenDrawerAfterCartSync = () =>
    document.body.classList.contains("regloss-cart-drawer-pending");

  const reglossOpenCartDrawerNow = () => {
    const drawer = document.querySelector("regloss-cart-drawer");
    if (drawer?.refreshFromServer) {
      drawer.refreshFromServer({ open: true }).catch((error) => {
        console.error(error);
        drawer.open?.();
      });
      return;
    }

    window.reglossPendingCartDrawerOpen = true;
  };

  const reglossOpenCartDrawer = () => {
    if (!reglossIsCartDrawerExperience()) return;

    if (customElements.get("regloss-cart-drawer")) {
      reglossOpenCartDrawerNow();
      return;
    }

    customElements.whenDefined("regloss-cart-drawer").then(() => {
      reglossOpenCartDrawerNow();
    });
  };

  const reglossOpenCartDrawerAfterCartSync = () => {
    window.setTimeout(reglossOpenCartDrawer, 150);
  };

  window.reglossOpenCartDrawer = reglossOpenCartDrawer;

  const reglossWrapLbSelleasyUtils = (utils = {}) => {
    const originalDrawerRefresh = utils.drawerRefresh?.bind(utils);
    return {
      ...utils,
      drawerRefresh(isDrawer) {
        if (reglossIsCartDrawerExperience()) {
          if (reglossShouldOpenDrawerAfterCartSync()) {
            reglossOpenCartDrawer();
          }
          reglossSchedulePostCartBundleSync({ refreshDrawer: true });
          return;
        }
        originalDrawerRefresh?.(isDrawer);
      },
    };
  };

  const reglossPatchSelleasyUtilsProperty = () => {
    if (window.__reglossLbUtilsPatched) return;
    window.__reglossLbUtilsPatched = true;

    let internalUtils = reglossWrapLbSelleasyUtils(
      window.lbSelleasyUtils || {},
    );

    Object.defineProperty(window, "lbSelleasyUtils", {
      configurable: true,
      enumerable: true,
      get() {
        return internalUtils;
      },
      set(value) {
        internalUtils = reglossWrapLbSelleasyUtils(value || {});
      },
    });
  };

  const reglossPatchLocationNavigation = () => {
    if (window.__reglossLocationNavigationPatched) return;
    window.__reglossLocationNavigationPatched = true;

    const interceptNavigation = (url) => {
      if (!reglossShouldSuppressCartRedirect(url)) return false;
      reglossArmCartRedirectSuppression();
      reglossOpenCartDrawer();
      return true;
    };

    const nativeAssign = window.location.assign.bind(window.location);
    const nativeReplace = window.location.replace.bind(window.location);

    window.location.assign = (url) => {
      if (interceptNavigation(url)) return;
      return nativeAssign(url);
    };

    window.location.replace = (url) => {
      if (interceptNavigation(url)) return;
      return nativeReplace(url);
    };

    const hrefDescriptor = Object.getOwnPropertyDescriptor(
      Location.prototype,
      "href",
    );
    if (hrefDescriptor?.set) {
      const nativeHrefSet = hrefDescriptor.set;
      Object.defineProperty(Location.prototype, "href", {
        ...hrefDescriptor,
        set(value) {
          if (interceptNavigation(value)) return;
          nativeHrefSet.call(this, value);
        },
      });
    }
  };

  const reglossPatchNavigationApi = () => {
    if (!window.navigation?.addEventListener) return;
    if (window.__reglossNavigationApiPatched) return;
    window.__reglossNavigationApiPatched = true;

    window.navigation.addEventListener("navigate", (event) => {
      const destinationUrl = event.destination?.url;
      if (!destinationUrl || !reglossShouldSuppressCartRedirect(destinationUrl))
        return;

      if (event.canIntercept) {
        event.intercept({
          handler() {
            reglossOpenCartDrawer();
          },
        });
        return;
      }

      if (event.cancelable) {
        event.preventDefault();
        reglossOpenCartDrawer();
      }
    });
  };

  const reglossPatchXmlHttpRequest = () => {
    if (window.__reglossXhrPatched) return;
    window.__reglossXhrPatched = true;

    const nativeOpen = XMLHttpRequest.prototype.open;
    const nativeSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this._reglossRequestUrl = typeof url === "string" ? url : String(url);
      return nativeOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function (...args) {
      const requestUrl = this._reglossRequestUrl || "";
      const isSelleasyCartAdd =
        /\/cart\/add/i.test(requestUrl) &&
        (requestUrl.includes("selleasy") ||
          requestUrl.includes("src=selleasy"));

      if (reglossIsCartDrawerExperience() && isSelleasyCartAdd) {
        reglossNoteBogosCartAdd();
        reglossArmCartRedirectSuppression();
        if (args[0]) {
          args[0] = reglossInjectRecommendBundleProperty(args[0]);
        }

        this.addEventListener(
          "loadend",
          () => {
            if (this.status < 200 || this.status >= 300) return;
            reglossBeginCartDrawerCheckoutFlow();
            reglossOpenCartDrawerAfterCartSync();
          },
          { once: true },
        );
      }

      /* Do not arm qty-suppress on every cart/change — BOGOS often updates the
         cart after ATC, which would permanently mute the free-gift popup. Qty
         suppress is armed only from drawer qty/remove UI handlers. */

      return nativeSend.apply(this, args);
    };
  };

  const reglossPatchFetch = () => {
    if (window.__reglossFetchPatched) return;
    window.__reglossFetchPatched = true;

    const nativeFetch = window.fetch.bind(window);

    window.fetch = (input, init = {}) => {
      const url = typeof input === "string" ? input : input?.url || "";

      if (reglossIsCartDrawerExperience() && /\/cart\/add/i.test(url)) {
        reglossNoteBogosCartAdd();
        reglossBeginCartDrawerCheckoutFlow();
        if (init.body) {
          init.body = reglossInjectRecommendBundleProperty(init.body);
        }
      }

      /* Qty suppress is not armed here — see XHR note above. */

      return nativeFetch(input, init);
    };
  };

  const REGLOSS_BOGOS_MODAL_SELECTOR =
    "#freegifts-main-popup-container, .freegifts-main-popup-container, [id*='freegifts-main-popup'], [class*='freegifts-main-popup'], .bogos-gifts-slider-container, .bogos-gift-slider-modal, [class*='bogos-gift-slider']";

  const REGLOSS_BOGOS_BLOCKER_SELECTOR = `${REGLOSS_BOGOS_MODAL_SELECTOR}, [class*='bogos-io'], [id*='bogos-io'], [class*='free-gift'], [id*='free-gift'], [class*='freegift'], [id*='freegift'], [class*='fg-gift'], [id*='fg-gift'], [class*='fg-slider'], [id*='fg-slider']`;

  const REGLOSS_BOGOS_QTY_SUPPRESS_MS = 8000;

  let reglossBogosDomObserver = null;
  let reglossBogosQtySuppressUntil = 0;
  let reglossBogosQtySuppressTimer = 0;
  /** 'add' allows gift after drawer close; 'change' never surfaces gift for that mutation. */
  let reglossBogosLastCartMutation = "";

  const reglossIsCartDrawerOpen = () =>
    document.body.classList.contains("regloss-cart-drawer-open");

  const reglossClearBogosQtySuppress = () => {
    window.clearTimeout(reglossBogosQtySuppressTimer);
    reglossBogosQtySuppressUntil = 0;
    document.body.classList.remove("regloss-bogos-qty-suppress");
  };

  const reglossNoteBogosCartAdd = () => {
    reglossBogosLastCartMutation = "add";
    reglossClearBogosQtySuppress();
  };

  /** Mute + discard gift UI after qty/line changes (do not restore those popups). */
  const reglossArmBogosQtySuppress = (
    durationMs = REGLOSS_BOGOS_QTY_SUPPRESS_MS,
  ) => {
    if (!reglossIsCartDrawerExperience()) return;
    reglossBogosLastCartMutation = "change";
    reglossBogosQtySuppressUntil = Date.now() + durationMs;
    document.body.classList.add("regloss-bogos-qty-suppress");
    reglossApplyBogosGiftPopupSuppression();
    window.clearTimeout(reglossBogosQtySuppressTimer);
    reglossBogosQtySuppressTimer = window.setTimeout(() => {
      reglossDismissBogosPopups();
      reglossClearBogosQtySuppress();
      /* Allow later browsing gifts again — only this mutation's popup was discarded. */
      if (reglossBogosLastCartMutation === "change") {
        reglossBogosLastCartMutation = "";
      }
      reglossApplyBogosGiftPopupSuppression();
    }, durationMs);
  };

  const reglossIsBogosQtySuppressActive = () =>
    document.body.classList.contains("regloss-bogos-qty-suppress") ||
    Date.now() <= reglossBogosQtySuppressUntil;

  /**
   * Hide gift UI only while ATC→drawer is pending, drawer is open, or a short
   * qty-suppress window is active. After drawer close on an add, gifts may show.
   */
  const reglossShouldSuppressBogosPopupNow = () => {
    if (!reglossIsCartDrawerExperience()) return false;
    if (document.body.classList.contains("regloss-cart-drawer-pending")) {
      return true;
    }
    if (reglossIsCartDrawerOpen()) return true;
    return reglossIsBogosQtySuppressActive();
  };

  const reglossIsInsideReglossCartDrawer = (node) =>
    Boolean(node?.closest?.("regloss-cart-drawer"));

  const reglossSuppressBogosNodes = (selector) => {
    document.querySelectorAll(selector).forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (reglossIsInsideReglossCartDrawer(node)) return;

      node.style.setProperty("display", "none", "important");
      node.style.setProperty("visibility", "hidden", "important");
      node.style.setProperty("pointer-events", "none", "important");
      node.setAttribute("data-regloss-bogos-suppressed", "true");
      node.setAttribute("aria-hidden", "true");
    });
  };

  const reglossRestoreBogosNodes = () => {
    document
      .querySelectorAll('[data-regloss-bogos-suppressed="true"]')
      .forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        node.style.removeProperty("display");
        node.style.removeProperty("visibility");
        node.style.removeProperty("pointer-events");
        node.removeAttribute("data-regloss-bogos-suppressed");
        node.removeAttribute("aria-hidden");
      });
  };

  /** Drop qty-triggered gift layers instead of un-hiding them later. */
  const reglossDismissBogosPopups = () => {
    document.querySelectorAll(REGLOSS_BOGOS_BLOCKER_SELECTOR).forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (reglossIsInsideReglossCartDrawer(node)) return;
      node.remove();
    });
  };

  const reglossStopBogosDomObserver = () => {
    reglossBogosDomObserver?.disconnect();
    reglossBogosDomObserver = null;
  };

  const reglossStartBogosDomObserver = () => {
    if (reglossBogosDomObserver || !document.body) return;

    reglossBogosDomObserver = new MutationObserver(() => {
      if (!reglossShouldSuppressBogosPopupNow()) {
        reglossStopBogosDomObserver();
        if (reglossBogosLastCartMutation !== "change") {
          reglossRestoreBogosNodes();
        }
        return;
      }
      reglossSuppressBogosNodes(REGLOSS_BOGOS_BLOCKER_SELECTOR);
    });

    reglossBogosDomObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    });
  };

  const reglossApplyBogosGiftPopupSuppression = () => {
    if (!reglossIsCartDrawerExperience()) return;

    if (reglossShouldSuppressBogosPopupNow()) {
      reglossSuppressBogosNodes(REGLOSS_BOGOS_BLOCKER_SELECTOR);
      reglossStartBogosDomObserver();
      return;
    }

    reglossStopBogosDomObserver();
    reglossRestoreBogosNodes();
  };

  const reglossPatchBogosGiftPopup = () => {
    if (window.__reglossBogosPopupPatched) return;
    window.__reglossBogosPopupPatched = true;

    document.addEventListener(
      "click",
      (event) => {
        if (!reglossIsCartDrawerExperience()) return;
        const qtyControl = event.target.closest(
          "regloss-cart-drawer .quantity__button, regloss-cart-drawer cart-remove-button, regloss-cart-drawer [name='plus'], regloss-cart-drawer [name='minus']",
        );
        if (!qtyControl) return;
        reglossArmBogosQtySuppress();
      },
      true,
    );

    document.addEventListener(
      "change",
      (event) => {
        if (!reglossIsCartDrawerExperience()) return;
        if (!event.target.closest?.("regloss-cart-drawer .quantity__input")) {
          return;
        }
        reglossArmBogosQtySuppress();
      },
      true,
    );

    document.addEventListener(
      "regloss-cart-drawer:opened",
      () => {
        reglossApplyBogosGiftPopupSuppression();
      },
      true,
    );

    document.addEventListener(
      "regloss-cart-drawer:closed",
      () => {
        reglossClearCartDrawerCheckoutFlow();
        if (reglossIsBogosQtySuppressActive()) {
          reglossDismissBogosPopups();
          reglossApplyBogosGiftPopupSuppression();
          return;
        }
        /* After ATC add: unhide gift popup that was held back while drawer was open. */
        reglossStopBogosDomObserver();
        reglossRestoreBogosNodes();
        reglossApplyBogosGiftPopupSuppression();
      },
      true,
    );

    document.addEventListener(
      "fg-gifts:show-slider",
      (event) => {
        if (!reglossShouldSuppressBogosPopupNow()) return;

        /* Qty changes: kill the popup. ATC→drawer: let BOGOS mount, then hide so
           restore on drawer close can reveal it. */
        if (reglossIsBogosQtySuppressActive()) {
          event.preventDefault?.();
          event.stopImmediatePropagation();
          reglossDismissBogosPopups();
          reglossApplyBogosGiftPopupSuppression();
          return;
        }

        requestAnimationFrame(() => {
          reglossApplyBogosGiftPopupSuppression();
        });
      },
      true,
    );

    ["fg-gifts:updated", "fg-cart:auto-updated"].forEach((eventName) => {
      document.addEventListener(
        eventName,
        () => {
          if (!reglossShouldSuppressBogosPopupNow()) return;
          if (reglossIsBogosQtySuppressActive()) {
            reglossDismissBogosPopups();
          }
          reglossApplyBogosGiftPopupSuppression();
        },
        true,
      );
    });
  };

  const reglossPatchCartNotification = () => {
    if (window.__reglossCartNotificationPatched) return;
    window.__reglossCartNotificationPatched = true;

    customElements.whenDefined("cart-notification").then(() => {
      const CartNotification = customElements.get("cart-notification");
      const originalRenderContents =
        CartNotification?.prototype?.renderContents;
      if (!originalRenderContents) return;

      CartNotification.prototype.renderContents = function (parsedState) {
        if (!reglossIsCartDrawerExperience()) {
          return originalRenderContents.call(this, parsedState);
        }

        reglossBeginCartDrawerCheckoutFlow();
        const drawer = document.querySelector("regloss-cart-drawer");
        if (drawer?.renderContents) {
          return drawer.renderContents(parsedState);
        }
      };
    });
  };

  const reglossInstallThirdPartyCartHooks = () => {
    if (window.__reglossThirdPartyCartHooks) return;
    window.__reglossThirdPartyCartHooks = true;

    reglossPatchBogosGiftPopup();
    reglossPatchCartNotification();
    reglossPatchFetch();
    reglossPatchSelleasyUtilsProperty();
    reglossPatchLocationNavigation();
    reglossPatchNavigationApi();
    reglossPatchXmlHttpRequest();

    window.addEventListener(
      "lbSECartUpdated",
      () => {
        void reglossRefreshBundleLinePropertiesCache();
        if (reglossShouldOpenDrawerAfterCartSync()) {
          reglossOpenCartDrawerAfterCartSync();
        }
      },
      true,
    );

    void reglossRefreshBundleLinePropertiesCache();
    if (reglossIsCartDrawerExperience()) {
      void reglossEnsureSellEasySetup();
    }

    document.addEventListener(
      "mousedown",
      (event) => {
        if (!reglossIsCartDrawerExperience()) return;
        if (event.target.closest(REGLOSS_BUNDLE_CTA_SELECTOR)) {
          reglossArmCartRedirectSuppression();
        }
      },
      true,
    );

    document.addEventListener(
      "click",
      (event) => {
        if (!reglossIsCartDrawerExperience()) return;
        if (event.target.closest(REGLOSS_BUNDLE_CTA_SELECTOR)) {
          reglossArmCartRedirectSuppression();
        }
      },
      true,
    );

    document.addEventListener(
      "click",
      (event) => {
        if (!reglossIsCartDrawerExperience()) return;
        const anchor = event.target.closest("a[href]");
        if (!anchor || !reglossShouldSuppressCartRedirect(anchor.href)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        reglossOpenCartDrawer();
      },
      true,
    );

    document.addEventListener(
      "submit",
      (event) => {
        if (!reglossIsCartDrawerExperience()) return;
        if (!event.target.closest("product-form")) return;
        reglossBeginCartDrawerCheckoutFlow();
      },
      true,
    );

    document.addEventListener("DOMContentLoaded", () => {
      if (typeof subscribe !== "function") return;
      subscribe("cart-error", () => {
        reglossClearCartDrawerCheckoutFlow();
      });
      if (typeof PUB_SUB_EVENTS !== "undefined") {
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          const drawerOpen =
            document.body.classList.contains("regloss-cart-drawer-open") ||
            document.body.classList.contains("regloss-cart-drawer-pending");
          reglossSchedulePostCartBundleSync({ refreshDrawer: drawerOpen });
        });
      }
    });
  };

  window.reglossApplyBogosGiftPopupSuppression =
    reglossApplyBogosGiftPopupSuppression;

  reglossInstallThirdPartyCartHooks();
})();
